'use client';

import {
  Box,
  Checkbox,
  CopyButton,
  Flex,
  Grid,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Transition,
} from '@mantine/core';
import Image from 'next/image';
import { useState } from 'react';

import { TrackedButton } from '@/components';
import { useWaitlist, WaitlistFormData } from '@/hooks';

import styles from './WaitlistForm.module.css';

interface WaitlistFormProps {
  className?: string;
  showLogo?: boolean;
  title?: string;
  subtitle?: string;
}

const WaitlistForm: React.FC<WaitlistFormProps> = ({
  className,
  showLogo = true,
  title = '',
  subtitle = '',
}: WaitlistFormProps) => {
  const [step, setStep] = useState<number>(1);
  const [waitlistId, setWaitlistId] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [formData, setFormData] = useState<WaitlistFormData>({
    email: '',
    firstName: '',
    lastName: '',
    issueDescription: '',
    organizationName: '',
    location: '',
    isIssueLeader: false,
  });
  const [error, setError] = useState<string>('');

  const { handleEmailSubmit, handleFullSubmit, addLoading, updateLoading } =
    useWaitlist();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const onEmailSubmit = async (): Promise<void> => {
    try {
      setError('');
      if (!validateEmail(formData.email)) {
        return;
      }

      const id = await handleEmailSubmit(formData.email);
      if (id) {
        setWaitlistId(id);
        setStep(2);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  const onFullSubmit = async (): Promise<void> => {
    try {
      setError('');
      // Add validation for required fields
      if (!formData.firstName.trim()) {
        setError('First name is required');
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Last name is required');
        return;
      }

      await handleFullSubmit(waitlistId, formData);
      setIsSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  const handleInputChange =
    (field: keyof WaitlistFormData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): void => {
      const target = event.target as HTMLInputElement;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (step === 1) {
      onEmailSubmit();
    } else {
      onFullSubmit();
    }
  };

  return (
    <div className={`${styles.formContainer} ${className || ''}`}>
      {showLogo && (
        <Box>
          <Image
            src="/assets/logo-header.svg"
            alt="Sway"
            width={200}
            height={50}
            className={styles.logo}
            priority
          />
        </Box>
      )}

      <Flex
        justify="center"
        align="center"
      >
        <Box className={styles.contentContainer}>
          <Stack gap="xs">
            {/* Title and subtitle */}
            {step === 1 && (
              <>
                <Title
                  order={1}
                  className={styles.mainTitle}
                >
                  {title}
                </Title>
                <Text className={styles.subtitle}>{subtitle}</Text>
              </>
            )}

            {/* Thank you message or form */}
            {isSubmitted ? (
              <Stack
                align="center"
                gap="xs"
              >
                <Text className={styles.formTitleLight}>
                  Thanks! We&apos;ll reach out soon.
                </Text>
                <CopyButton value="https://www.sway.co">
                  {({ copied, copy }) => (
                    <TrackedButton
                      variant="secondary"
                      size="lg"
                      className={styles.shareButton}
                      onClick={copy}
                      trackingName="WaitlistForm_share_button"
                    >
                      {copied ? 'Link Copied!' : 'Share'}
                    </TrackedButton>
                  )}
                </CopyButton>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit}>
                {step === 1 ? (
                  <div className={styles.emailRow}>
                    <TextInput
                      placeholder="Email Address"
                      className={styles.emailField}
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={error}
                      classNames={{
                        error: styles.errorText,
                      }}
                    />
                    <TrackedButton
                      type="submit"
                      variant="secondary"
                      size="md"
                      loading={addLoading}
                      trackingName="WaitlistForm_join_waitlist_button"
                    >
                      Join Waitlist
                    </TrackedButton>
                  </div>
                ) : (
                  <Transition
                    mounted={step === 2}
                    transition="scale-y"
                    duration={300}
                    timingFunction="ease"
                  >
                    {(scaleStyles) => (
                      <Stack style={{ ...scaleStyles, marginTop: '1rem' }}>
                        <Title
                          order={3}
                          className={styles.formTitle}
                        >
                          You&apos;re on the waitlist!
                        </Title>

                        <Group grow>
                          <TextInput
                            placeholder="First Name*"
                            value={formData.firstName}
                            onChange={handleInputChange('firstName')}
                          />
                          <TextInput
                            placeholder="Last Name*"
                            value={formData.lastName}
                            onChange={handleInputChange('lastName')}
                          />
                        </Group>

                        <Grid
                          justify="start"
                          align="center"
                        >
                          <Grid.Col span={{ base: 12, md: 8 }}>
                            <TextInput
                              placeholder="What causes are most important to you?"
                              value={formData.issueDescription}
                              onChange={handleInputChange('issueDescription')}
                            />
                          </Grid.Col>

                          <Grid.Col span={{ base: 12, md: 4 }}>
                            <Checkbox
                              styles={{
                                body: {
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                },
                                icon: {
                                  color: 'var(--mantine-color-primary-9)',
                                },
                              }}
                              label="I'm interested in being a leader for this cause."
                              checked={formData.isIssueLeader}
                              onChange={handleInputChange('isIssueLeader')}
                            />
                          </Grid.Col>
                        </Grid>

                        <TextInput
                          placeholder="Where are you located?"
                          value={formData.location}
                          onChange={handleInputChange('location')}
                        />

                        <TrackedButton
                          type="submit"
                          variant="secondary"
                          size="lg"
                          loading={updateLoading}
                          className={styles.submitButton}
                          trackingName="WaitlistForm_request_early_access_button"
                        >
                          Request Leader Access
                        </TrackedButton>

                        {error && (
                          <Text className={styles.errorText}>{error}</Text>
                        )}
                      </Stack>
                    )}
                  </Transition>
                )}
              </form>
            )}
          </Stack>
        </Box>
      </Flex>
    </div>
  );
};

export { WaitlistForm };
